import {
  creneauxLibres,
  instantDepuisHeureMurale,
  jourMural,
  minutesMurales,
  occupeLeCreneau,
  plancherDuJour,
  uuidv7,
  type Creneau,
  type Horaires,
  type RendezVous,
  type RendezVousInput,
  type StatutRdv,
} from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'
import { enqueue } from '../sync/engine.js'

/**
 * Les rendez-vous, côté appareil.
 *
 * ⭐ Lus dans la RÉPLIQUE, jamais par appel réseau : proposer un créneau est un
 * geste de comptoir, fait devant le client. Une caissière qui doit attendre une
 * réponse du serveur pour dire « jeudi 14 h ? » perd la conversation.
 */

/**
 * Un blocage tel que la RÉPLIQUE le porte.
 *
 * ⚠️ Plus pauvre que le `Blocage` du serveur : ni nom de catégorie ni auteur.
 * La grille résout la catégorie dans sa propre liste, déjà répliquée, et
 * l'auteur ne sert qu'à l'écran d'administration, toujours en ligne.
 */
export interface BlocageLocal {
  id: string
  categoryId: string
  /** Instants ISO : la conversion en heure murale appartient à l'affichage. */
  debut: string
  fin: string
  postes: number
  motif: string | null
}

function toRendezVous(row: LocalRow): RendezVous {
  return {
    id: String(row.id),
    customerId: (row['customer_id'] as string) ?? null,
    customerName: (row['customer_name'] as string) ?? null,
    itemId: (row['item_id'] as string) ?? null,
    transactionId: (row['transaction_id'] as string) ?? null,
    posteId: (row['poste_id'] as string) ?? null,
    transactionRef: (row['transaction_ref'] as string) ?? null,
    label: String(row['label'] ?? ''),
    startsAt: String(row['starts_at'] ?? ''),
    endsAt: String(row['ends_at'] ?? ''),
    status: (row['status'] as StatutRdv) ?? 'booked',
    notes: (row['notes'] as string) ?? null,
    createdAt: String(row['created_at'] ?? ''),
    updatedAt: String(row['updated_at'] ?? ''),
    version: Number(row['version'] ?? 1),
  }
}

/**
 * Les rendez-vous d'une fenêtre de temps.
 *
 * ⚠️ On retient tout ce qui CHEVAUCHE la fenêtre, pas seulement ce qui y
 * commence : une prestation de 14 h à 15 h 30 appartient à la journée qu'on
 * regarde, même si la fenêtre s'arrête à 15 h. La filtrer ferait disparaître de
 * l'écran un créneau pourtant occupé — et l'on proposerait par-dessus.
 */
export async function rendezVousLocaux(
  storeId: string,
  debut: string,
  fin: string,
  options: { avecAnnules?: boolean; customerId?: string } = {},
): Promise<RendezVous[]> {
  const db = dbFor(storeId)
  const [rows, clients] = await Promise.all([db.appointments.toArray(), db.customers.toArray()])

  /**
   * ⭐ Le NOM du client, retrouvé dans la réplique.
   *
   * ⚠️ Il n'est PAS recopié dans le rendez-vous par la synchronisation : le
   * planning affichait donc « Client » pour tout le monde, ce qui ne sert à
   * rien quand on décroche le téléphone. Le rapprochement se fait ici, sur la
   * table des clients déjà répliquée — et le nom suit la fiche quand elle est
   * corrigée, ce qu'une copie figée n'aurait pas fait.
   */
  const nomDe = (c: Record<string, unknown>): string => {
    const complet = `${c['first_name'] ?? ''} ${c['last_name'] ?? ''}`.trim()
    // Une société n'a ni prénom ni nom : c'est sa raison sociale qui l'appelle.
    return complet || String(c['company_name'] ?? '').trim()
  }
  const noms = new Map(clients.map((c) => [String(c.id), nomDe(c)]))

  return rows
    .map(toRendezVous)
    .map((r) => ({
      ...r,
      customerName: r.customerName ?? (r.customerId ? (noms.get(r.customerId) || null) : null),
    }))
    .filter((r) => r.startsAt < fin && r.endsAt > debut)
    .filter((r) => options.avecAnnules || r.status !== 'cancelled')
    .filter((r) => !options.customerId || r.customerId === options.customerId)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
}

/**
 * Les blocages qui TOUCHENT une période, lus dans la réplique.
 *
 * ⚠️ Chevauchement à bornes strictes, comme côté serveur : un blocage qui
 * s'achève à l'instant où la période commence ne ferme rien.
 */
export async function blocagesLocaux(
  storeId: string,
  debut: string,
  fin: string,
): Promise<BlocageLocal[]> {
  const rows = await dbFor(storeId).blocages.toArray()
  return rows
    .map((r) => ({
      id: String(r.id),
      categoryId: String(r['category_id'] ?? ''),
      debut: String(r['debut'] ?? ''),
      fin: String(r['fin'] ?? ''),
      postes: Number(r['postes'] ?? 0),
      motif: (r['motif'] as string) ?? null,
    }))
    .filter((b) => b.debut < fin && b.fin > debut)
    .sort((a, b) => a.debut.localeCompare(b.debut))
}

/**
 * Les créneaux libres d'une journée, pour une durée donnée.
 *
 * ⚠️ La conversion instant → heure murale se fait ICI, une seule fois, avec le
 * fuseau de la boutique. Le calcul lui-même (`creneauxLibres`) n'a jamais vu de
 * `Date` : c'est ce qui le rend testable et insensible au changement d'heure.
 */
export async function creneauxDuJour(
  storeId: string,
  /**
   * ⚠️ Une DATE CIVILE « AAAA-MM-JJ », pas un `Date`.
   *
   * Un `Date` porte un instant, donc un fuseau : « minuit le 14 » chez le poste
   * vaut 23 h le 13 à Douala, et toute la journée se décalait d'un cran — les
   * rendez-vous du 14 étaient rangés au 13, donc absents du calcul, donc leurs
   * créneaux reproposés. Une date civile n'a pas ce défaut : elle ne dit rien
   * d'autre que le jour dont on parle.
   */
  jour: string,
  horaires: Horaires,
  dureeMinutes: number,
  fuseau: string,
  /**
   * Un rendez-vous à IGNORER dans les occupations.
   *
   * ⚠️ Indispensable pour un déplacement : sans lui, le rendez-vous qu'on
   * déplace se bloque lui-même, et l'écran n'offrirait aucun créneau autour de
   * son horaire actuel — y compris celui d'où il vient.
   */
  ignorer?: string,
  /**
   * Le POSTE dont on cherche les disponibilités.
   *
   * ⚠️ Sans lui, le calcul répondait pour la boutique entière : un fauteuil
   * occupé rendait les deux autres indisponibles, et l'écran offrait dix fois
   * moins de créneaux qu'il n'en restait vraiment. La base, elle, aurait
   * accepté la réservation — les deux se contredisaient.
   */
  posteId?: string,
): Promise<Creneau[]> {
  const debutJour = instantDepuisHeureMurale(jour, '00:00', fuseau)
  const finJour = new Date(new Date(debutJour).getTime() + 24 * 3600 * 1000).toISOString()

  const pris = await rendezVousLocaux(storeId, debutJour, finJour)
  const occupes = pris
    .filter(
      (r) =>
        r.id !== ignorer &&
        occupeLeCreneau(r.status) &&
        jourMural(r.startsAt, fuseau) === jour &&
        // Un poste demandé : seules SES occupations comptent. Aucun poste
        // demandé : on retombe sur l'ancienne règle, la boutique entière.
        (posteId === undefined || r.posteId === posteId),
    )
    .map((r) => ({
      debut: minutesMurales(r.startsAt, fuseau),
      fin: minutesMurales(r.endsAt, fuseau),
    }))

  return creneauxLibres({
    horaires,
    // Midi : n'importe quelle heure de la journée donne le bon jour de semaine,
    // sauf les bornes, où le fuseau du poste peut faire basculer la date.
    jourSemaine: new Date(`${jour}T12:00:00`).getDay(),
    duree: dureeMinutes,
    occupes,
    // ⚠️ La règle COMMUNE à la grille : un jour passé n'offre plus rien (il en
    // offrait tout, faute d'être « aujourd'hui »), aujourd'hui s'arrête à
    // l'heure de la boutique.
    maintenant: plancherDuJour(jour, fuseau),
  })
}

/**
 * Pose un rendez-vous.
 *
 * ⭐ Écrit d'abord dans la réplique, puis enfile la mutation : l'écran montre
 * le créneau pris immédiatement, et le serveur tranchera. Si un autre poste l'a
 * réservé entre-temps, la mutation revient « rejetée » avec son motif —
 * l'écran des conflits le dit, et la caissière rappelle le client. C'est
 * désagréable, mais honnête : l'alternative serait de bloquer la prise de
 * rendez-vous hors ligne, c'est-à-dire de la refuser quand elle est le plus
 * utile.
 */
export async function poserRendezVous(
  storeId: string,
  entree: Omit<RendezVousInput, 'id'>,
): Promise<RendezVous> {
  const db = dbFor(storeId)
  const id = uuidv7()
  const maintenant = new Date().toISOString()

  const ligne: LocalRow = {
    id,
    customer_id: entree.customerId,
    item_id: entree.itemId ?? null,
    transaction_id: entree.transactionId ?? null,
    label: entree.label,
    starts_at: entree.startsAt,
    ends_at: entree.endsAt,
    status: entree.status ?? 'booked',
    notes: entree.notes ?? null,
    created_at: maintenant,
    updated_at: maintenant,
    version: 1,
    _dirty: 1,
  }

  await db.appointments.put(ligne)
  await enqueue(storeId, {
    entity: 'appointment',
    entityId: id,
    op: 'create',
    baseVersion: null,
    payload: { ...entree, id },
  })

  return toRendezVous(ligne)
}

/**
 * Annule — statut, jamais suppression : la trace commerciale reste.
 *
 * ⚠️ La charge ne porte QUE le statut, et la version est relue dans la
 * réplique.
 *
 * Envoyer l'objet entier — ce que faisait la première version — transformait
 * en CONFLIT toute écriture concurrente : annuler juste après avoir déplacé le
 * rendez-vous se heurtait au déplacement qu'on venait soi-même d'accepter,
 * puisque les deux charges portaient `startsAt`. Le serveur détecte les
 * collisions champ par champ et sait fusionner ce qui est disjoint ; encore
 * faut-il ne lui déclarer que ce qu'on change réellement.
 *
 * Prendre la version dans l'objet reçu était la seconde moitié du défaut : cet
 * objet vient d'un rendu antérieur, et sa version a pu vieillir entre-temps.
 */
export async function annulerRendezVous(storeId: string, rdv: RendezVous): Promise<void> {
  const db = dbFor(storeId)
  const ligne = await db.appointments.get(rdv.id)
  if (!ligne) return

  await db.appointments.put({ ...ligne, status: 'cancelled', _dirty: 1 })
  await enqueue(storeId, {
    entity: 'appointment',
    entityId: rdv.id,
    op: 'update',
    baseVersion: Number(ligne['version'] ?? rdv.version),
    payload: { status: 'cancelled' },
  })
}

/**
 * Confirme un rendez-vous pris EN LIGNE.
 *
 * ⭐ Le geste que la boutique fait après avoir vu la demande : « oui, on vous
 * attend ». Le créneau était déjà bloqué — la validation ne réserve rien de
 * plus, elle engage la boutique.
 */
export async function validerRendezVous(storeId: string, rdv: RendezVous): Promise<void> {
  const db = dbFor(storeId)
  const ligne = await db.appointments.get(rdv.id)
  if (!ligne) return

  await db.appointments.put({ ...ligne, status: 'booked', _dirty: 1 })
  await enqueue(storeId, {
    entity: 'appointment',
    entityId: rdv.id,
    op: 'update',
    baseVersion: Number(ligne['version'] ?? rdv.version),
    payload: { status: 'booked' },
  })
}

/**
 * Les rendez-vous indexés par document de vente.
 *
 * ⭐ Sert au journal des ventes : une vente qui bloque un créneau ne se
 * distinguait en rien d'une vente comptoir, alors que la différence est
 * entière — l'une est due un jour précis, l'autre est déjà servie.
 *
 * ⚠️ Les ANNULÉS sont écartés : un rendez-vous décommandé ne doit plus
 * s'afficher sous la vente, sans quoi le journal annoncerait un rendez-vous
 * qui n'aura pas lieu.
 */
export async function rendezVousParVente(storeId: string): Promise<Map<string, RendezVous>> {
  const rows = await dbFor(storeId).appointments.toArray()
  const carte = new Map<string, RendezVous>()
  for (const row of rows) {
    const rdv = toRendezVous(row)
    if (rdv.transactionId && rdv.status !== 'cancelled') carte.set(rdv.transactionId, rdv)
  }
  return carte
}

/**
 * Tous les rendez-vous d'un client — passés, présents et à venir.
 *
 * ⚠️ Sans fenêtre de temps, contrairement à l'agenda : la fiche client répond
 * à « qu'a-t-on fait avec cette personne, et que lui a-t-on promis ? ». Borner
 * la lecture au mois en cours y masquerait justement l'historique qu'on vient
 * chercher.
 *
 * ⭐ Les ANNULÉS sont conservés, à l'inverse du planning : sur une fiche
 * client, « s'est décommandé trois fois » est une information commerciale, pas
 * du bruit. Le tri est ANTICHRONOLOGIQUE — le plus récent d'abord, comme
 * partout ailleurs dans l'application.
 */
export async function rendezVousDuClient(
  storeId: string,
  customerId: string,
): Promise<RendezVous[]> {
  const rows = await dbFor(storeId).appointments.toArray()
  return rows
    .map(toRendezVous)
    .filter((r) => r.customerId === customerId)
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt))
}

/**
 * Déplace un rendez-vous — mêmes client, prestation et document, autre horaire.
 *
 * ⭐ Un DÉPLACEMENT, pas une annulation suivie d'une reprise : le client n'a
 * pas renoncé, il vient un autre jour. Le distinguer compte pour la boutique —
 * « trois reports » ne se lit pas comme « trois annulations » — et pour le
 * client, dont l'invitation d'agenda est mise à jour au lieu d'être retirée
 * puis réémise.
 *
 * ⚠️ La DURÉE vient du rendez-vous, pas de l'article : allonger la prestation
 * au catalogue ne doit pas rallonger un rendez-vous déjà convenu avec quelqu'un.
 */
export async function deplacerRendezVous(
  storeId: string,
  rdv: RendezVous,
  debut: string,
  fin: string,
): Promise<void> {
  const db = dbFor(storeId)
  const ligne = await db.appointments.get(rdv.id)
  if (!ligne) return

  await db.appointments.put({ ...ligne, starts_at: debut, ends_at: fin, _dirty: 1 })
  // Seules les BORNES changent : le client, la prestation et le document
  // restent ceux du serveur, et une annulation concurrente ne s'y heurte pas.
  await enqueue(storeId, {
    entity: 'appointment',
    entityId: rdv.id,
    op: 'update',
    baseVersion: Number(ligne['version'] ?? rdv.version),
    payload: { startsAt: debut, endsAt: fin },
  })
}
